import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { useDotLegalPostGetQueryArray } from "@common/hooks/dotLegalReactQuery";
import { EntityType } from "@common/Common.types";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { GetDateData, GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { DateFieldValue, getDateRangeFromValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { hasDateRange } from "@common/components/filterBar/filterOperatorMatching";
import { IdNamePair } from "../../customLists/customListView/CustomListView.types";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { organisationTypeFilterId, subjectTypeFilterOptions } from "../riskFilterUtils";
import { getRiskLevelColor } from "../riskLevelColor";
import { getRiskLevelIcon } from "../riskLevelIcon";
import { entityTypeLabel } from "../riskOperationalOverview/RiskOperationalOverview.types";
import { eventKindTranslationKey, RiskHistoryEvent, RiskHistoryEventKind, RiskHistoryRequest, RiskHistoryResult } from "./RiskHistoryOverview.types";

const defaultPageSize = 50;
const searchUrl = "/risk-areas/history/search";

const eventKindFilterId = "eventKinds";
// Must match the backend FilterDataHandler.RiskEntitiesFilterId so the active-filter chip can resolve
// entity names via /filter-data (the same id the operational/mitigation overviews use). The request
// itself maps selectedEntityIds to request.entityIds, so this UI id is independent of the payload shape.
const entityFilterId = "risk-entity-ids";
const entityTypeFilterId = "entityTypes";
const periodFilterId = "period";

// Reuses the operational overview's authorized entity search (id/name across the assessable types).
const entitySearchUrl = "/risk-areas/operational/entities";

const allEventKinds: Array<RiskHistoryEventKind> = [
    RiskHistoryEventKind.Assessed,
    RiskHistoryEventKind.Reassessed,
    RiskHistoryEventKind.Reconfirmed,
    RiskHistoryEventKind.MitigationPromoted,
    RiskHistoryEventKind.ScaleMigrated,
    RiskHistoryEventKind.Retired,
    RiskHistoryEventKind.Reinstated,
    RiskHistoryEventKind.PlanProposed,
    RiskHistoryEventKind.PlanApproved,
    RiskHistoryEventKind.PlanCompleted,
    RiskHistoryEventKind.RiskAccepted,
    RiskHistoryEventKind.AreaConfigChanged,
    RiskHistoryEventKind.RiskAvoided,
    RiskHistoryEventKind.PlanStarted,
    RiskHistoryEventKind.PlanPlanned,
    RiskHistoryEventKind.PlanDeferred,
    RiskHistoryEventKind.PlanReactivated,
    RiskHistoryEventKind.PlanCancelled,
    RiskHistoryEventKind.MatrixRebased,
];

function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

// Converts the persisted date-range filter into a stable from/to range for the request. Both explicit
// dates and the relative presets are snapped to whole-day boundaries (start of `from`, end of `to`) so
// the value is identical across renders within a day — a sub-day-varying range (e.g. a preset that ends
// at "now") would change the React Query key every render and trigger an endless refetch loop. Snapping
// `to` to the end of the day also makes the chosen "to" date inclusive.
function dateRangeToRequest(value: DateFieldValue | null): { from?: string; to?: string } {
    if (!value) return {};

    const range = getDateRangeFromValue(value);
    if (!range) return {};

    return {
        from: range.from ? startOfDay(range.from).toISOString() : undefined,
        to: range.to ? endOfDay(range.to).toISOString() : undefined,
    };
}

export function useRiskHistoryOverview(riskAreaId: string | undefined) {
    const { translateString } = useTranslation();
    const { riskAreas } = useRiskAreasContext();

    // Filter selections persist per area in session storage (like the other overviews) so they survive
    // switching away to another tab/page and back. Page number stays ephemeral — it resets on any filter change.
    const areaKey = riskAreaId ?? "";
    const [selectedEventKinds, setSelectedEventKinds] = useStateSessionStorage<Array<string>>(`risk-history-event-kinds:${areaKey}`, []);
    const [selectedEntityIds, setSelectedEntityIds] = useStateSessionStorage<Array<string>>(`risk-history-entities:${areaKey}`, []);
    const [selectedEntityTypes, setSelectedEntityTypes] = useStateSessionStorage<Array<string>>(`risk-history-entity-types:${areaKey}`, []);
    const [dateRange, setDateRange] = useStateSessionStorage<DateFieldValue | null>(`risk-history-period:${areaKey}`, null);
    const [searchText, setSearchText] = useStateSessionStorage<string>(`risk-history-search:${areaKey}`, "");
    const [pinnedFilterIds, setPinnedFilterIds] = useState<Array<string>>([]);
    const onPinnedFilterIdsChange = (ids: Array<string> | undefined) => setPinnedFilterIds(ids ?? []);
    const [pageNumber, setPageNumber] = useState(1);

    // Deep-link from the governance "Viewing as of {date}" banner: seed the period's "to" date from the
    // historyTo query param once on mount (overwriting any stored period — the user followed the link to see
    // up to that date), then strip the param so a later manual filter change isn't re-applied on refresh.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const historyTo = searchParams.get("historyTo");
        if (!historyTo) return;
        setDateRange({ from: undefined, to: new Date(historyTo), dateFieldEnum: undefined });
        searchParams.delete("historyTo");
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The row whose single-assessment timeline dialog is open (null when closed). Ephemeral view state,
    // like the page number — it never needs to survive a tab switch.
    const [selectedEvent, setSelectedEvent] = useState<RiskHistoryEvent | null>(null);

    const riskAreaName = riskAreas.find((x) => x.id === riskAreaId)?.name;

    const selectedTypeValues = selectedEntityTypes.filter((type) => type !== organisationTypeFilterId);

    const request: RiskHistoryRequest = {
        riskAreaId: riskAreaId ?? "",
        eventKinds: selectedEventKinds.length > 0 ? selectedEventKinds.map((x) => Number(x) as RiskHistoryEventKind) : undefined,
        entityIds: selectedEntityIds.length > 0 ? selectedEntityIds : undefined,
        entityTypes: selectedTypeValues.length > 0 ? selectedTypeValues.map((x) => Number(x) as EntityType) : undefined,
        // The organisation is one of the filter's options but not an EntityType, so it travels beside the list.
        // Only ever sent as true: this filter has no negation, so "not the organisation" cannot be expressed.
        includeOrganisationSubject: selectedEntityTypes.includes(organisationTypeFilterId) ? true : undefined,
        ...dateRangeToRequest(dateRange),
        searchText: searchText.trim() || undefined,
        pageNumber,
        pageSize: defaultPageSize,
    };

    const { data, isLoading } = useDotLegalPostGetQueryArray<RiskHistoryResult>([searchUrl, request], searchUrl, request);

    const resetToFirstPage = () => setPageNumber(1);

    const eventKindOptions: Array<IdNamePair> = allEventKinds.map((kind) => ({
        id: kind.toString(),
        name: translateString(eventKindTranslationKey[kind]),
    }));

    const entityTypeOptions = subjectTypeFilterOptions(translateString);

    const filterOptions: Array<FilterOption> = [
        {
            id: eventKindFilterId,
            label: translateString("riskHistoryEvent"),
            type: FilterOptionType.SelectField,
            value: selectedEventKinds,
            fixedData: eventKindOptions,
        },
        {
            id: entityFilterId,
            label: translateString("entity"),
            type: FilterOptionType.SelectField,
            value: selectedEntityIds,
            searchUrl: entitySearchUrl,
        },
        {
            id: entityTypeFilterId,
            label: translateString("type"),
            type: FilterOptionType.SelectField,
            value: selectedEntityTypes,
            fixedData: entityTypeOptions,
        },
        {
            id: periodFilterId,
            label: translateString("riskHistoryPeriod"),
            type: FilterOptionType.DateField,
            value: dateRange ?? "",
            hideNoDate: true,
            hideFutureDates: true,
        },
    ];

    const onFilterChange = (updatedOptions: Array<FilterOption>) => {
        updatedOptions.forEach((option) => {
            switch (option.id) {
                case eventKindFilterId:
                    setSelectedEventKinds(GetListData(option.value));
                    break;
                case entityFilterId:
                    setSelectedEntityIds(GetListData(option.value));
                    break;
                case entityTypeFilterId:
                    setSelectedEntityTypes(GetListData(option.value));
                    break;
                case periodFilterId: {
                    const dateValue = GetDateData(option.value);
                    const hasRange = hasDateRange(dateValue);
                    setDateRange(hasRange ? dateValue : null);
                    break;
                }
            }
        });
        resetToFirstPage();
    };

    const onSearchTextChange = (value: string) => {
        setSearchText(value ?? "");
        resetToFirstPage();
    };

    const getEventLabel = (kind: RiskHistoryEventKind) => translateString(eventKindTranslationKey[kind]);

    const getEntityTypeLabel = (type: EntityType | null) => {
        return type != null ? entityTypeLabel(type, translateString) : "";
    };

    // The audit narrative for the "Why" column: the proposal's plan description, a status/acceptance reason,
    // else the rating justifications. Both the proposal description and the justifications are rich text (a
    // promoted revision copies the plan's HTML description into its justifications), so they render as formatted
    // HTML — matching the timeline dialog — while the plain-text acceptance/status reason renders as text.
    const getWhy = (event: RiskHistoryEvent): { value: string; isHtml: boolean } => {
        if (event.eventKind === RiskHistoryEventKind.PlanProposed && event.description) return { value: event.description, isHtml: true };
        if (event.reason) return { value: event.reason, isHtml: false };
        const justifications = [event.consequenceJustification, event.probabilityJustification].filter((x): x is string => !!x);
        return { value: justifications.join(" · "), isHtml: true };
    };

    const getRiskColor = (level: number, scale: number) => getRiskLevelColor(level, scale);
    const getRiskIcon = (level: number, scale: number) => getRiskLevelIcon(level, scale);

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        riskAreaName,
        result: data,
        isLoading,
        pageNumber,
        pageSize: defaultPageSize,
        onPageChange: setPageNumber,
        filterOptions,
        onFilterChange,
        searchText,
        onSearchTextChange,
        getEventLabel,
        getEntityTypeLabel,
        getWhy,
        getRiskColor,
        getRiskIcon,
        selectedEvent,
        openTimeline: setSelectedEvent,
        closeTimeline: () => setSelectedEvent(null),
    };
}
